import { NavigationEvent, useAnalyticsContext, useLocalStorageState } from '@faro/app-context';
import { FaroIconButton, FaroNavigationLink } from '@faro/design-system';
import { AppsIcon, FaroLogoIconIcon, FaroLogoTextIcon, VerticalArrowsInIcon } from '@faro/icons';
import { ComponentPropsWithRef, ReactNode, useMemo } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router';
import styles from './SideNavHeader.module.scss';

export interface LinkData {
    id: string;
    variant?: string;
    label: ReactNode;
    intercomLabel?: string;
    title?: string;
    icon: ReactNode;
    url: string;
}

export interface SideNavHeaderProps extends ComponentPropsWithRef<'div'> {
    links?: LinkData[];
    open?: boolean;
}

export enum SideNavHeaderAnalyticEventKey {
    navigateTo = 'Navigate to',
}

export interface SideNavHeaderAnalyticEventPayload {
    [SideNavHeaderAnalyticEventKey.navigateTo]: NavigationEvent;
}

const trackingPayloadUrlToNameMap: Record<string, string> = {
    '/spaces': 'Study Spaces',
    '/studies': 'Study Designer',
    '/libraries': 'Content Library',
    '/user-management': 'User Management',
    '/protocol-import': 'SoA Import',
    '/full-protocol-import': 'Protocol Import (Lab)',
    '/protocol-optimization': 'Protocol Optimization',
};

function SideNavHeader(props: SideNavHeaderProps): ReactNode {
    const { children, className = '', links = [], open, ref, ...rest } = props;
    const [expanded, setExpanded] = useLocalStorageState<boolean>('faro.side-nav.links-expanded', false);
    const location = useLocation();
    const activeLink = useMemo(
        () => links.find(link => matchPath({ path: link?.url + '/*', end: false }, location.pathname)),
        [location.pathname, links]
    );
    const { intercomLabel: _activeIntercomLabel, ...activeLinkProps } = activeLink ?? {};

    const { createTracker } = useAnalyticsContext();
    const trackEvent = createTracker<SideNavHeaderAnalyticEventPayload>();

    function trackLink(newUrl: string) {
        const previousURL = `/${window.location.pathname.split('/')[1] ?? ''}`;

        if (newUrl in trackingPayloadUrlToNameMap && previousURL in trackingPayloadUrlToNameMap) {
            const from = trackingPayloadUrlToNameMap[previousURL];
            const to = trackingPayloadUrlToNameMap[newUrl];
            const payload: NavigationEvent = { product: from, to };
            trackEvent(SideNavHeaderAnalyticEventKey.navigateTo, payload);
        }
    }

    return (
        <div
            {...rest}
            ref={ref}
            className={`
                ${className} 
                ${styles.sideNavHeader} 
                ${expanded ? styles.expanded : ''}
                ${open ? styles.open : ''}
            `}
            style={
                {
                    '--link-count': links.length,
                } as React.CSSProperties
            }
            data-automation-id="sideNavHeader"
        >
            <div className={`${styles.links}`}>
                <div className={`${styles.link} ${styles.top}`}>
                    <NavLink to={activeLink?.url || '/'}>
                        <FaroNavigationLink
                            {...activeLinkProps}
                            className={`${styles.linkNavItem}`}
                            variant={links[0]?.variant}
                            showIconOnly={!open}
                            iconLeft={<FaroLogoIconIcon className={styles.navIcon} />}
                            label={
                                <div className={styles.homeLinkLabel}>
                                    <FaroLogoTextIcon preserveAspectRatio="xMinYMid meet" />
                                </div>
                            }
                        />
                    </NavLink>

                    {open && (
                        <FaroIconButton
                            data-automation-id="verticalArrows"
                            onClick={() => setExpanded(!expanded)}
                            className={styles.dropdownButton}
                            appearance="inline"
                            icon={
                                <>
                                    <VerticalArrowsInIcon
                                        className={`${styles.icon} ${styles.overlayedIcon} ${
                                            expanded ? styles.visible : ''
                                        } `}
                                    />
                                    <AppsIcon className={`${styles.icon} ${expanded ? '' : styles.visible}`} />
                                </>
                            }
                        />
                    )}
                </div>
                {links.map(linkProps => {
                    const { icon, url, intercomLabel, ...rest } = linkProps;
                    return (
                        <div
                            key={rest.id}
                            className={styles.link}
                            data-automation-id={rest.id}
                            data-testid={rest.id}
                            data-intercom-target={`Navigate to ${intercomLabel ?? rest.label}`}
                        >
                            <NavLink to={url} tabIndex={expanded ? 0 : -1}>
                                {({ isActive }) => (
                                    <FaroNavigationLink
                                        isActive={isActive}
                                        iconLeft={<div className={styles.navIcon}>{icon}</div>}
                                        className={styles.linkNavItem}
                                        showIconOnly={!open}
                                        {...rest}
                                        onClick={() => trackLink(url)}
                                    />
                                )}
                            </NavLink>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default SideNavHeader;
